// Case workflow timing — when each step of the firm's process is due.
//
// The firm's SOPs are already in the app as 59 steps across six matter
// templates, in order. What they never carried is timing: every due
// offset was empty, so every task landed on a matter with no date, and
// nothing could ever be "due today". The SOP documents do not fill the
// gap either — three explicit intervals across four documents, one of
// them about rental cars. So this is a decision the firm makes, and
// this screen is where it makes it.
//
// A SUGGESTION IS NOT A VALUE. Every suggested number is rendered as a
// placeholder, never as a filled field, and nothing is stored until
// Save. A screen that pre-filled 59 numbers would be saved unread, and
// the app would then be asserting that MCB's process is whatever a
// lookup table in the codebase guessed. Progress is shown as "n of 59
// set" so an unfinished screen looks unfinished.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const ROLE_LABELS = {
  support_staff: "Support staff",
  paralegal: "Paralegal",
  associate: "Associate",
  partner: "Partner",
};

export function WorkflowTimingSection({ showNotification }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await apiFetch("/api/firm/workflow-timing");
      setData(d);
      setEdits({});
      if (d.templates?.length && openTemplate === null) {
        setOpenTemplate(d.templates[0].template_id);
      }
    } catch (e) {
      setError(e.message);
      setData({ templates: [], total_steps: 0, configured_steps: 0 });
    }
  }, [openTemplate]);

  // Once, on mount. `load` is a useCallback over openTemplate, so
  // listing it here would re-fetch every time a template is expanded.
  // The bare eslint-disable that used to sit here named no rule and
  // silenced nothing.
  useEffect(() => { load(); }, []);

  const dirty = Object.keys(edits).length;

  const stage = (id, field, value) =>
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(edits).map(([id, u]) => ({
        id: Number(id),
        ...(u.due_offset_days !== undefined
          ? { due_offset_days: u.due_offset_days === "" ? null
              : Number(u.due_offset_days) } : {}),
        ...(u.assignee_role !== undefined ? { assignee_role: u.assignee_role } : {}),
        ...(u.due_anchor_task_id !== undefined
          ? { due_anchor_task_id: u.due_anchor_task_id || 0 } : {}),
        ...(u.repeat_every_days !== undefined
          ? { repeat_every_days: u.repeat_every_days === "" ? null
              : Number(u.repeat_every_days) } : {}),
      }));
      const res = await apiFetch("/api/firm/workflow-timing", {
        method: "PATCH",
        body: JSON.stringify({ updates }),
      });
      showNotification && showNotification(
        `Saved timing on ${res.updated} step${res.updated === 1 ? "" : "s"}.`,
        "success");
      await load();
    } catch (e) {
      showNotification && showNotification("Could not save: " + e.message, "error");
    }
    setSaving(false);
  };

  // Fill every empty offset in the open template from its suggestion.
  // Staged like any other edit, so it still has to be reviewed and
  // saved — this is a way to start, not a way to skip.
  const applySuggestions = (tpl) => {
    const next = { ...edits };
    let n = 0;
    for (const s of tpl.steps) {
      const staged = next[s.id] || {};
      const currentOffset = staged.due_offset_days !== undefined
        ? staged.due_offset_days : s.due_offset_days;
      if (s.suggested && (currentOffset === null || currentOffset === "")) {
        next[s.id] = {
          ...staged,
          due_offset_days: s.suggested.due_offset_days,
          assignee_role: staged.assignee_role !== undefined
            ? staged.assignee_role : (s.assignee_role || s.suggested.assignee_role),
        };
        n += 1;
      }
    }
    setEdits(next);
    showNotification && showNotification(
      n === 0 ? "Nothing left to suggest in this list."
              : `${n} suggestion${n === 1 ? "" : "s"} filled in — review, then Save.`,
      "info");
  };

  if (data === null) {
    return React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center", padding: "20px 0" },
    },
      React.createElement(Spinner, { size: 14 }),
      React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
        "Loading the firm's process…"));
  }

  const pct = data.total_steps
    ? Math.round((data.configured_steps / data.total_steps) * 100) : 0;

  return React.createElement("div", { style: { marginTop: "34px" } },
    React.createElement("h2", {
      style: { fontFamily: "'Playfair Display',serif", fontSize: "20px",
               color: C.navy, marginBottom: "4px" },
    }, "Case workflow timing"),
    React.createElement("p", {
      style: { fontSize: "13.5px", color: C.textMuted, lineHeight: 1.6,
               marginBottom: "14px", maxWidth: "680px" },
    }, "Your SOPs are already in the app as the steps below, in order. "
     + "What they do not say is when each one is due — so until a step "
     + "has a number here, tasks created from it have no due date and "
     + "cannot appear on anyone's day. Days are counted from when the "
     + "list is applied to a matter, unless you point a step at an "
     + "earlier one — then it becomes a follow-up that only gets a date "
     + "once that step is done. A repeat interval makes a step come "
     + "back until the system marks it done for you."),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "14px" },
    }, error),

    // Progress. An unfinished screen should look unfinished.
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "12px",
               marginBottom: "18px", flexWrap: "wrap" },
    },
      React.createElement("div", {
        style: { fontSize: "13px", color: C.textDark, fontWeight: 600 },
      }, `${data.configured_steps} of ${data.total_steps} steps have a due date`),
      React.createElement("div", {
        style: { flex: 1, minWidth: "120px", height: "6px", borderRadius: "3px",
                 background: C.warmGray, overflow: "hidden", maxWidth: "260px" },
      },
        React.createElement("div", {
          style: { width: `${pct}%`, height: "100%", background: C.navy },
        }),
      ),
      dirty > 0 && React.createElement("button", {
        onClick: save, disabled: saving,
        style: { border: "none", background: C.navy, color: "#fff",
                 borderRadius: "8px", padding: "9px 18px", fontSize: "13px",
                 fontWeight: 600, cursor: saving ? "default" : "pointer" },
      }, saving ? "Saving…" : `Save ${dirty} change${dirty === 1 ? "" : "s"}`),
    ),

    ...data.templates.map((tpl) => {
      const open = openTemplate === tpl.template_id;
      return React.createElement("div", {
        key: tpl.template_id,
        style: { border: `1px solid ${C.warmGray}`, borderRadius: "10px",
                 marginBottom: "10px", background: "#fff", overflow: "hidden" },
      },
        React.createElement("div", {
          className: "wrap-row",
          style: { display: "flex", alignItems: "center", gap: "10px",
                   padding: "12px 16px", cursor: "pointer" },
          onClick: () => setOpenTemplate(open ? null : tpl.template_id),
        },
          React.createElement("span", {
            style: { fontSize: "14px", fontWeight: 600, color: C.navy, flex: 1 },
          }, tpl.template_name),
          React.createElement("span", {
            style: { fontSize: "12px", color: C.textMuted },
          }, `${tpl.configured}/${tpl.total} set`),
          React.createElement("span", {
            style: { fontSize: "12px", color: C.textLight },
          }, open ? "Hide" : "Edit"),
        ),

        open && React.createElement("div", {
          style: { borderTop: `1px solid ${C.warmGray}` },
        },
          React.createElement("div", {
            style: { padding: "10px 16px", borderBottom: `1px solid ${C.warmGray}55` },
          },
            React.createElement("button", {
              onClick: () => applySuggestions(tpl),
              style: { border: `1px solid ${C.warmGray}`, background: "#fff",
                       borderRadius: "6px", padding: "6px 12px", fontSize: "12px",
                       fontWeight: 600, color: C.navy, cursor: "pointer" },
            }, "Fill empty steps with suggestions"),
            React.createElement("span", {
              style: { fontSize: "11.5px", color: C.textMuted, marginLeft: "10px" },
            }, "Ordinary personal-injury sequencing, not legal advice. "
             + "Nothing is stored until you save."),
          ),

          ...tpl.steps.map((s, i) => React.createElement(StepRow, {
            key: s.id, step: s, first: i === 0,
            edit: edits[s.id], onChange: stage,
            roles: data.assignable_roles || [],
            // Only earlier steps, so the dropdown cannot offer a
            // dependency that runs backwards through the list.
            siblings: tpl.steps.filter(
              (o) => o.id !== s.id && o.sort_order < s.sort_order),
          })),
        ),
      );
    }),
  );
}


function StepRow({ step, first, edit, onChange, roles, siblings }) {
  const offset = edit?.due_offset_days !== undefined
    ? edit.due_offset_days
    : (step.due_offset_days ?? "");
  const role = edit?.assignee_role !== undefined
    ? edit.assignee_role
    : (step.assignee_role || "");
  const anchor = edit?.due_anchor_task_id !== undefined
    ? (edit.due_anchor_task_id || "")
    : (step.due_anchor_task_id || "");
  const repeat = edit?.repeat_every_days !== undefined
    ? edit.repeat_every_days
    : (step.repeat_every_days ?? "");
  const touched = edit !== undefined;

  return React.createElement("div", {
    className: "stack-row",
    style: {
      display: "flex", gap: "12px", alignItems: "center",
      padding: "10px 16px",
      borderTop: first ? "none" : `1px solid ${C.warmGray}55`,
      background: touched ? C.warmWhite : "transparent",
    },
  },
    React.createElement("span", {
      style: { flex: 1, minWidth: 0, fontSize: "13px", color: C.textDark,
               lineHeight: 1.4 },
    }, step.title,
      step.suggested && React.createElement("div", {
        style: { fontSize: "11px", color: C.textLight, marginTop: "2px" },
      }, step.suggested.why),
    ),

    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 },
    },
      React.createElement("input", {
        type: "number", min: 0, max: 3650, value: offset,
        // Placeholder, not value — an empty field stays empty, and a
        // suggestion never counts as a decision.
        placeholder: step.suggested ? String(step.suggested.due_offset_days) : "—",
        onChange: (e) => onChange(step.id, "due_offset_days", e.target.value),
        style: { width: "68px", padding: "6px 8px", fontSize: "13px",
                 border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                 textAlign: "right" },
      }),
      React.createElement("span", {
        style: { fontSize: "11px", color: C.textMuted },
      }, "days"),
    ),

    // What the offset is measured from. Default is "when the list is
    // applied"; pointing it at an earlier step turns the row into a
    // follow-up that only gets a date once that step is done.
    React.createElement("select", {
      value: anchor,
      onChange: (e) => onChange(step.id, "due_anchor_task_id",
                                e.target.value === "" ? 0 : Number(e.target.value)),
      title: "What the days are counted from",
      style: { padding: "6px 8px", fontSize: "12.5px", borderRadius: "6px",
               border: `1px solid ${C.warmGray}`, background: "#fff",
               color: anchor ? C.navy : C.textLight, flexShrink: 0,
               maxWidth: "190px" },
    },
      React.createElement("option", { value: "" }, "after list applied"),
      ...siblings.map((o) => React.createElement("option",
        { key: o.id, value: o.id },
        `after "${o.title.length > 30 ? o.title.slice(0, 30) + "…" : o.title}"`)),
    ),

    React.createElement("input", {
      type: "number", min: 1, max: 3650, value: repeat,
      placeholder: "repeat",
      title: "Repeat every N days until it is completed by the system",
      onChange: (e) => onChange(step.id, "repeat_every_days", e.target.value),
      style: { width: "74px", padding: "6px 8px", fontSize: "13px",
               border: `1px solid ${C.warmGray}`, borderRadius: "6px",
               textAlign: "right", flexShrink: 0 },
    }),

    React.createElement("select", {
      value: role,
      onChange: (e) => onChange(step.id, "assignee_role", e.target.value),
      style: { padding: "6px 8px", fontSize: "12.5px", borderRadius: "6px",
               border: `1px solid ${C.warmGray}`, background: "#fff",
               color: role ? C.navy : C.textLight, flexShrink: 0,
               minWidth: "130px" },
    },
      React.createElement("option", { value: "" },
        step.suggested ? `${ROLE_LABELS[step.suggested.assignee_role]} (suggested)`
                       : "Nobody yet"),
      ...roles.map((r) => React.createElement("option", { key: r, value: r },
        ROLE_LABELS[r] || r)),
    ),
  );
}
